import { AnimatePresence, motion } from 'framer-motion';
import { FC, ReactNode, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  AddDocumentIcon,
  ChevronDownIcon,
  EditIcon,
  EyeIconSmall,
  LockIcon,
  PinIcon,
  PlusIcon,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../../../assets/icons/index';
import { PlusIconVariant } from '../../../../assets/icons/plus-icon';
import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button, { ButtonDisableStyle } from '../../../../components/button/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/popover/index';
import { Switch } from '../../../../components/switch/index';
import { QueryKey } from '../../../../constants/index';
import {
  Company,
  EventHooks,
  SafeHooks,
  useFormat,
  useInvalidateQueries,
  useLocale,
  useModalState,
} from '../../../../hooks/index';
import {
  CompanyIncorporationEvent,
  eventFormType,
  EventTypeBackend,
  EventUnion,
  EventUnionWithPermissions,
  FundraisingEvent,
  GrantEvent,
  SortByDate,
} from '../../../../types/events.types';
import { SharePlanType } from '../../../../types/pool-plans.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../utils/company-storage-url.util';
import { toRound } from '../../../../utils/get-rounded-number.util';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { objectTypeCheck } from '../../../../utils/object-type-check.util';
import { toNumber } from '../../../../utils/to-number.util';
import { EditPlanModal } from '../../../pool-plans/plan-form/edit/index';
import { EditPoolModal } from '../../../pool-plans/pool-form/index';
import { EventSignatureStatusCell } from '../../components/event-signature-status-cell';
import { DeleteEventModal, FilesModal } from '../../modals/index';
import { Tag, TagVariant } from '../../tag';
import { getEventTransactionDate } from '../../utils';
import {
  isEventsModuleExerciseEditEnabled,
  isExerciseBackedEventType,
} from '../../utils/exercise-event-edit-enabled.util';
import {
  getPoolAdjustmentTitle,
  isPoolAdjustmentEvent,
  isPoolEvent,
} from '../../utils/pool-event-row.util';
import { resolveEventLevelDocumentLinks } from '../../utils/resolve-event-document-links.util';
import { BlockedTooltipAction } from './blocked-action-tooltip';
import { Approval } from './event-table-types/approval';
import { Buyback } from './event-table-types/buyback';
import { CompanyIncorporation } from './event-table-types/company-incorporation';
import { Conversion } from './event-table-types/conversion';
import { ConvertedSafe } from './event-table-types/converted-safe';
import { TableBodyProps } from './event-table-types/event-table-types.types';
import { Exercised } from './event-table-types/exercise';
import { Expire } from './event-table-types/expire';
import { FundraisingRound } from './event-table-types/fundraising-round';
import { GrantBody } from './event-table-types/grant';
import { NewPlan } from './event-table-types/new-plan';
import { Payout } from './event-table-types/payout';
import { PoolEvents } from './event-table-types/pool-events';
import { Safe } from './event-table-types/safe';
import { Secondaries } from './event-table-types/secondaries';
import { ShareIssuance } from './event-table-types/share-issuance';
import { Valuation } from './event-table-types/valuation';

const tableBodyConstructor: Record<EventTypeBackend, (props: TableBodyProps) => ReactNode> = {
  [EventTypeBackend.GRANT]: (props) => <GrantBody {...props} />,
  [EventTypeBackend.FUNDRAISING_ROUND]: (props) => <FundraisingRound {...props} />,
  [EventTypeBackend.SHARE_ISSUANCE]: (props) => <ShareIssuance {...props} />,
  [EventTypeBackend.SECONDARIES]: (props) => <Secondaries {...props} />,
  [EventTypeBackend.BUYBACK]: (props) => <Buyback {...props} />,
  [EventTypeBackend.VALUATION]: (props) => <Valuation {...props} />,
  [EventTypeBackend.NEW_POOL]: (props) => <PoolEvents {...props} />,
  [EventTypeBackend.POOL_INCREASE]: (props) => <PoolEvents {...props} />,
  [EventTypeBackend.POOL_DECREASE]: (props) => <PoolEvents {...props} />,
  [EventTypeBackend.CONVERSION]: (props) => <Conversion {...props} />,
  [EventTypeBackend.COMPANY_INCORPORATION]: (props) => <CompanyIncorporation {...props} />,
  [EventTypeBackend.NEW_PLAN]: (props) => <NewPlan {...props} />,
  [EventTypeBackend.EXERCISE]: (props) => <Exercised {...props} />,
  [EventTypeBackend.APPROVAL]: (props) =>
    props.transactionView ? <Exercised {...props} /> : <Approval {...props} />,
  [EventTypeBackend.PAYOUT]: (props) => <Payout {...props} />,
  [EventTypeBackend.EXPIRE]: (props) => <Expire {...props} />,
  [EventTypeBackend.SAFE_CONVERSION]: (props) => <ConvertedSafe {...props} />,
  [EventTypeBackend.SAFE]: (props) => <Safe {...props} />,
};

export type EventItemProps = {
  companyId: string;
  event: EventUnionWithPermissions;
  onAddInvestment: () => void;
  onCloseRound: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onTransactionDelete: (event: EventUnion, transactionId: string) => void;
  onTransactionEdit: (id: string, transactionId: string) => void;
  onTransactionView: (id: string, transactionId: string) => void;
  onView: () => void;
  refetch: () => void;
  selectedCustomDateFilter: DateRange;
  selectedDateFilter: SortByDate;
  transactionView?: boolean;
};

export const EventItem: FC<EventItemProps> = ({
  event,
  onDelete,
  onEdit,
  onView,
  onTransactionDelete,
  onTransactionView,
  onTransactionEdit,
  companyId,
  selectedDateFilter,
  selectedCustomDateFilter,
  onAddInvestment,
  onCloseRound,
  transactionView,
}) => {
  const { messagesLocale } = useLocale();
  const { selectedCompany: { currency } = {} } = Company.useSelected();
  const { type } = event;
  const { allLinks: eventDocumentFileLinks, docusignLinks: eventDocusignFileLinks } =
    resolveEventLevelDocumentLinks(event);

  const transactionDate = getEventTransactionDate(event);
  const isFundraisingRound = objectTypeCheck<FundraisingEvent>(event, 'isOpen');

  const isOpen = isFundraisingRound ? event.isOpen : false;

  const mergedEventUnion = event as MergeUnion<EventUnion>;
  const [isExpanded, setExpanded] = useState(false);
  const { format } = useFormat();

  const { deleteConversion } = SafeHooks.useDeleteConversion();

  const handleDeleteSafeConversion = () => {
    if (event.type !== EventTypeBackend.SAFE_CONVERSION) return;

    deleteConversion(event.conversion.id || '', {
      onSuccess: () => {
        toast.success(messagesLocale['SAFE_DELETED_SUCCESS']);
      },
    });
  };

  const {
    handleCloseModal,
    handleOpenModal,
    handleSuccessModal,
    isOpen: isOpenModal,
    toggler,
  } = useModalState({
    // SAFE events route through onDelete like every other type so they get the queued-delete
    // pending row and settle-time toasts (the shell Event cascades from the Safe on the backend).
    onSuccess: () => {
      if (event.type !== EventTypeBackend.SAFE_CONVERSION) return onDelete();
      handleDeleteSafeConversion();
    },
  });

  const handleCloseRound = () => {
    onCloseRound();
  };

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleSuccessModal: handleSuccessFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState({ onSuccess: () => handleDownloadAllDocuments(eventDocumentFileLinks) });

  const {
    handleCloseModal: handleCloseEditPoolModal,
    handleOpenModal: handleOpenEditPoolModal,
    isOpen: isOpenEditPoolModal,
  } = useModalState({});

  const {
    handleCloseModal: handleCloseEditPlanModal,
    handleOpenModal: handleOpenEditPlanModal,
    isOpen: isOpenEditPlanModal,
  } = useModalState({});

  const transactions = mergedEventUnion.items || [1];

  const exerciseTransactions =
    event.type === EventTypeBackend.EXERCISE ||
    event.type === EventTypeBackend.APPROVAL ||
    event.type === EventTypeBackend.PAYOUT ||
    event.type === EventTypeBackend.EXPIRE
      ? event.exercise?.items?.length
      : 0;

  const { pinEvent } = EventHooks.usePin({ companyId });
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_EVENTS,
    QueryKey.GET_EVENT,
    QueryKey.GET_COMPANY_INCORPORATION_CONTEXT,
    QueryKey.GET_COMPANY_GET_STARTED_PROGRESS,
  );
  const originalName =
    mergedEventUnion.type !== EventTypeBackend.SAFE_CONVERSION
      ? mergedEventUnion.valuation?.name || mergedEventUnion.name
      : undefined;

  // Pool and plan events are owned by Pools & Plans: View Details only — no edit, pin or delete.
  const isPoolOrPlan = type === EventTypeBackend.NEW_PLAN || isPoolEvent(event);
  const shouldRenderEventId =
    type === EventTypeBackend.SAFE_CONVERSION ||
    type === EventTypeBackend.BUYBACK ||
    type === EventTypeBackend.GRANT ||
    type === EventTypeBackend.CONVERSION ||
    type === EventTypeBackend.SECONDARIES ||
    type === EventTypeBackend.SHARE_ISSUANCE ||
    type === EventTypeBackend.EXERCISE ||
    type === EventTypeBackend.APPROVAL ||
    type === EventTypeBackend.PAYOUT ||
    type === EventTypeBackend.EXPIRE;
  const eventDisplayId = (
    type === EventTypeBackend.SAFE_CONVERSION
      ? event.conversion.id
      : type === EventTypeBackend.GRANT
        ? event.id
        : event.eventId
  )
    .split('-')[0]
    .toUpperCase();
  const isPhantomPayout =
    type === EventTypeBackend.PAYOUT &&
    event.exercise?.items[0]?.vestingTask?.grantItem?.plan?.type === SharePlanType.PHANTOM;

  const canEditEventDetails = event.canEdit && isEventsModuleExerciseEditEnabled(event);

  const exerciseEditDetailsBlockedMessageKey =
    event.canEdit && isExerciseBackedEventType(type) && !isEventsModuleExerciseEditEnabled(event)
      ? 'EVENTS_MODULE_EXERCISE_EDIT_DISABLED'
      : undefined;

  const name = isPhantomPayout ? (
    <AppFormattedMessage id="PHANTOM_PAYOUT" />
  ) : isPoolAdjustmentEvent(event) ? (
    getPoolAdjustmentTitle(event, messagesLocale[eventFormType[type]])
  ) : (
    originalName || messagesLocale[eventFormType[type]]
  );

  const haveOneTransaction = type === EventTypeBackend.VALUATION || isPoolOrPlan;
  const isExerciseEvent =
    type === EventTypeBackend.EXERCISE ||
    type === EventTypeBackend.EXPIRE ||
    type === EventTypeBackend.APPROVAL ||
    type === EventTypeBackend.PAYOUT;

  const shouldRenderPrePostValuation =
    type === EventTypeBackend.FUNDRAISING_ROUND || type === EventTypeBackend.SHARE_ISSUANCE;

  const isCompanyIncorporation = type === EventTypeBackend.COMPANY_INCORPORATION;
  const companyIncorporationEvent = isCompanyIncorporation
    ? (event as CompanyIncorporationEvent & EventUnionWithPermissions)
    : null;
  const companyIncorporationTotalShares =
    companyIncorporationEvent?.items?.reduce((sum, item) => sum + item.sharesCount, 0) ?? 0;
  const companyIncorporationParValue = toNumber(companyIncorporationEvent?.parValue ?? 0) ?? 0;

  const postMoneyValuation = toNumber(
    toRound(
      (mergedEventUnion.valuation?.sharePrice || 0) *
        (mergedEventUnion.valuation?.issuedShares || 0),
      2,
    ),
  );

  const preValuation: number = toNumber(
    toRound(
      (mergedEventUnion.valuation?.sharePrice || 0) *
        (mergedEventUnion.valuation?.issuedSharesOnStart || 0),
    ),
  );

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(companyId, links);
  };

  const isNotSafe = type !== EventTypeBackend.SAFE_CONVERSION && type !== EventTypeBackend.SAFE;

  if (transactionView)
    return (
      <div className="flex w-full flex-col overflow-x-auto rounded-md bg-white px-2 pt-3 shadow-sm">
        {tableBodyConstructor[type]({
          ...event,
          selectedCustomDateFilter,
          selectedDateFilter,
          onView: onTransactionView,
          onDelete: onTransactionDelete,
          onEdit: onTransactionEdit,
          companyId,
          name: typeof name === 'string' ? name : typeof name === 'function' ? name : '', //weird
          inCurrentRound: event.canEdit && event.canDelete,
          transactionView,
        })}
      </div>
    );

  return (
    <div className="h-full w-full">
      <EditPoolModal
        companyId={companyId}
        docusignFileLinks={eventDocusignFileLinks}
        handleClose={handleCloseEditPoolModal}
        handleTerminate={() => ''}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenEditPoolModal}
        onEditClick={() => ''}
        poolId={isPoolEvent(event) ? event.pool.id : ''}
        previewMode={true}
        step={3}
      />
      <EditPlanModal
        companyId={companyId}
        docusignFileLinks={eventDocusignFileLinks}
        handleClose={handleCloseEditPlanModal}
        handleTerminate={() => ''}
        invalidateQuery={invalidateQuery}
        isOpenModal={isOpenEditPlanModal}
        onEditClick={() => ''}
        planId={event.type === EventTypeBackend.NEW_PLAN ? event.sharePlan.id : ''}
        previewMode={true}
        step={5}
      />
      <AlertDialogWrapper control={{ onOpenChange: toggler, open: isOpenModal }}>
        <DeleteEventModal
          hasSignatures={
            type === EventTypeBackend.GRANT
              ? !!(event as GrantEvent).hasSignatures || !!(event as GrantEvent).selectedTemplateId
              : undefined
          }
          onClose={handleCloseModal}
          onSuccess={handleSuccessModal}
          type={type}
        />
      </AlertDialogWrapper>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={transactionDate}
          docusignFileLinks={eventDocusignFileLinks}
          filesLinks={eventDocumentFileLinks}
          name={name}
          onClose={handleCloseFilesModal}
          onDownloadFile={handleDocumentClick}
          onSuccess={handleSuccessFilesModal}
        />
      </AlertDialogWrapper>
      <div
        className={twMerge(
          'flex w-full cursor-pointer flex-col rounded-md border border-gray-100 transition-colors duration-200',
          isExpanded ? 'bg-gray-50' : 'hover:bg-brand-25/30',
        )}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <div
          className={twMerge('flex w-full items-center justify-between border-gray-100 px-4 py-3')}
        >
          <div className="flex w-full max-w-[calc(100%-500px)] flex-col">
            <div className="flex w-fit items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <span className="truncate text-base font-semibold text-gray-700">{name}</span>
              {event.type === EventTypeBackend.FUNDRAISING_ROUND && (
                <>
                  <Tag
                    title={<AppFormattedMessage id={isOpen ? 'OPEN_ROUND' : 'CLOSED_ROUND'} />}
                    variant={isOpen ? TagVariant.GREEN : TagVariant.RED}
                  />
                  <AnimatePresence data-permission-required>
                    {isOpen && (
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        initial={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Switch
                          data-permission-required
                          disableChange
                          enabled={isOpen}
                          onChange={handleCloseRound}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>
            <div
              className="flex w-fit items-center gap-2 text-label-sm font-medium text-gray-500"
              onClick={(e) => e.stopPropagation()}
            >
              {type === EventTypeBackend.NEW_PLAN && (
                <>
                  <span>{event.sharePlan.name}</span>
                  <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                </>
              )}
              {type === EventTypeBackend.NEW_POOL && (
                <>
                  <span>{event.pool.name}</span>
                  <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                </>
              )}
              <span>{format(transactionDate, 'dd/MM/yyyy')}</span>
              <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
              <span>
                {haveOneTransaction
                  ? 1
                  : isExerciseEvent
                    ? exerciseTransactions
                    : transactions.length}{' '}
                {(transactions.length || exerciseTransactions) > 1 ? (
                  <AppFormattedMessage id="TRANSACTIONS" />
                ) : (
                  <AppFormattedMessage id="TRANSACTION" />
                )}
              </span>
              {shouldRenderEventId && (
                <>
                  <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                  <span>
                    <AppFormattedMessage id="EVENT_ID" /> {eventDisplayId}
                  </span>
                </>
              )}
              {event.pinned && (
                <>
                  <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                  <HoverCard>
                    <HoverCardTrigger
                      data-permission-required
                      onClick={(e) => {
                        e.stopPropagation();
                        pinEvent({ eventId: event.eventId, pinState: false });
                      }}
                    >
                      <PinIcon className="rotate-45" iconColor="#2565C8" />
                    </HoverCardTrigger>
                    {/* Same top placement as the files tooltip in this header row. */}
                    <HoverCardContent
                      className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                      side="top"
                    >
                      <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                        <AppFormattedMessage id="UNPIN" />
                      </span>
                    </HoverCardContent>
                  </HoverCard>
                </>
              )}
              {event.signatureSummary && (
                <>
                  <span className="size-[3px] shrink-0 rounded-full bg-gray-300" />
                  <EventSignatureStatusCell
                    signatureSignedCount={event.signatureSummary.signatureSignedCount}
                    signatureStatus={event.signatureSummary.signatureStatus}
                    signatureTotalCount={event.signatureSummary.signatureTotalCount}
                  />
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div
              className="flex cursor-default items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {shouldRenderPrePostValuation && (
                <Tag
                  borderColor="border-brand-200"
                  title={`${messagesLocale['PRE_MONEY_VALUATION']}: ${preValuation.toLocaleString('en-US')}`}
                  variant={TagVariant.BLUE}
                />
              )}
              {shouldRenderPrePostValuation && (
                <Tag
                  borderColor="border-forest-200"
                  title={`${messagesLocale['POST_MONEY_VALUATION']}: ${postMoneyValuation.toLocaleString('en-US')}`}
                  variant={TagVariant.GREEN}
                />
              )}
              {type === EventTypeBackend.VALUATION && (
                <Tag
                  borderColor="border-brand-200"
                  title={`${messagesLocale['PRE_MONEY_VALUATION']}: ${((mergedEventUnion.issuedSharesOnStart || 0) * (mergedEventUnion.sharePrice || 0)).toLocaleString('en-US')}`}
                  variant={TagVariant.BLUE}
                />
              )}
              {isCompanyIncorporation && (
                <>
                  <Tag
                    borderColor="border-brand-200"
                    title={`${messagesLocale['TOTAL_SHARES']}: ${companyIncorporationTotalShares.toLocaleString('en-US')}`}
                    variant={TagVariant.BLUE}
                  />
                  <Tag
                    borderColor="border-forest-200"
                    title={`${messagesLocale['PAR_VALUE']}: ${currency?.symbol ?? '$'}${companyIncorporationParValue.toLocaleString('en-US', { maximumFractionDigits: 7 })}`}
                    variant={TagVariant.GREEN}
                  />
                </>
              )}
            </div>
            <HoverCard>
              <HoverCardTrigger onClick={(e) => e.stopPropagation()}>
                <AddDocumentIcon
                  className="size-6 cursor-pointer p-[2px]"
                  onClick={() =>
                    eventDocumentFileLinks.length > 0 &&
                    (eventDocumentFileLinks.length === 1
                      ? handleDocumentClick(eventDocumentFileLinks[0])
                      : handleOpenFilesModal())
                  }
                />
              </HoverCardTrigger>
              {/* side="top": Radix defaults to bottom, which drops the tooltip over the event's
                  transaction table; the default arrow styling already points down for a top card. */}
              <HoverCardContent
                className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                side="top"
              >
                <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                  {eventDocumentFileLinks.length === 1 ? (
                    getS3FileOriginalName(eventDocumentFileLinks[0])
                  ) : (
                    <AppFormattedMessage
                      id="ATTACHED_FILES"
                      values={{ count: eventDocumentFileLinks.length }}
                    />
                  )}
                </span>
              </HoverCardContent>
            </HoverCard>
            <Popover>
              <PopoverTrigger
                className="h-fit w-fit rounded data-[state=open]:shadow-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
                  <ThreeDotsIcon />
                </div>
              </PopoverTrigger>
              <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                {isNotSafe && (
                  <PopoverClose asChild onClick={(e) => e.stopPropagation()}>
                    <div
                      className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={
                        !isPoolOrPlan
                          ? onView
                          : isPoolEvent(event)
                            ? handleOpenEditPoolModal
                            : handleOpenEditPlanModal
                      }
                    >
                      <EyeIconSmall />
                      <span className="text-sm font-normal text-gray-700">
                        <AppFormattedMessage id="VIEW_DETAILS" />
                      </span>
                    </div>
                  </PopoverClose>
                )}
                {!isPoolOrPlan && (
                  <>
                    {type === EventTypeBackend.FUNDRAISING_ROUND && isOpen && (
                      <BlockedTooltipAction allowed={event.canEdit}>
                        <PopoverClose asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                            data-permission-required
                            disabled={!event.canEdit}
                            disabledStyle={ButtonDisableStyle.NONE}
                            onClick={onAddInvestment}
                            styleType="NONE"
                          >
                            <PlusIcon
                              iconColor={event.canEdit ? '#344054' : '#98A2B3'}
                              variant={PlusIconVariant.EVENTS}
                            />
                            <span
                              className={twMerge(
                                'text-sm font-normal text-gray-700',
                                !event.canEdit && 'text-gray-400',
                              )}
                            >
                              <AppFormattedMessage id="ADD_INVESTMENT" />
                            </span>
                          </Button>
                        </PopoverClose>
                      </BlockedTooltipAction>
                    )}
                    {isNotSafe && (
                      <BlockedTooltipAction
                        allowed={canEditEventDetails}
                        messageKey={exerciseEditDetailsBlockedMessageKey}
                        type={type}
                      >
                        <PopoverClose asChild onClick={(e) => e.stopPropagation()}>
                          <Button
                            className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                            data-permission-required
                            disabled={!canEditEventDetails}
                            disabledStyle={ButtonDisableStyle.NONE}
                            onClick={onEdit}
                            styleType="NONE"
                          >
                            <EditIcon iconColor={canEditEventDetails ? undefined : '#98A2B3'} />
                            <span
                              className={twMerge(
                                'text-sm font-normal text-gray-700',
                                !canEditEventDetails && 'text-gray-400',
                              )}
                            >
                              <AppFormattedMessage
                                id={
                                  type === EventTypeBackend.FUNDRAISING_ROUND
                                    ? 'EDIT_EVENT'
                                    : 'EDIT_DETAILS'
                                }
                              />
                            </span>
                          </Button>
                        </PopoverClose>
                      </BlockedTooltipAction>
                    )}
                    <PopoverClose asChild onClick={(e) => e.stopPropagation()}>
                      <div
                        className="flex w-full cursor-pointer items-center gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                        data-permission-required
                        onClick={() => {
                          pinEvent({ eventId: event.eventId, pinState: !event.pinned });
                        }}
                      >
                        <PinIcon />
                        <span className="text-sm font-normal text-gray-700">
                          {event.pinned ? (
                            <AppFormattedMessage id="UNPIN" />
                          ) : (
                            <AppFormattedMessage id="PIN_EVENT" />
                          )}
                        </span>
                      </div>
                    </PopoverClose>

                    {type === EventTypeBackend.FUNDRAISING_ROUND && isOpen && (
                      <PopoverClose asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                          data-permission-required
                          disabledStyle={ButtonDisableStyle.NONE}
                          onClick={handleCloseRound}
                          styleType="NONE"
                        >
                          <LockIcon iconColor={'#344054'} width={16} />
                          <span className={twMerge('text-sm font-normal text-gray-700')}>
                            <AppFormattedMessage id="CLOSE_ROUND" />
                          </span>
                        </Button>
                      </PopoverClose>
                    )}

                    <BlockedTooltipAction allowed={event.canDelete} type={type}>
                      <PopoverClose asChild onClick={(e) => e.stopPropagation()}>
                        <Button
                          className="flex w-full items-center justify-start gap-2 rounded p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                          data-permission-required
                          disabled={!event.canDelete}
                          disabledStyle={ButtonDisableStyle.NONE}
                          onClick={handleOpenModal}
                          styleType="NONE"
                        >
                          <TrashBinIcon iconColor={event.canDelete ? '#F04438' : '#98A2B3'} />
                          <span
                            className={twMerge(
                              'text-sm font-normal text-fireside-500',
                              !event.canDelete && 'text-gray-400',
                            )}
                          >
                            <AppFormattedMessage id="DELETE_EVENT" />
                          </span>
                        </Button>
                      </PopoverClose>
                    </BlockedTooltipAction>
                  </>
                )}
              </PopoverContent>
            </Popover>
            <div className={twMerge('flex w-fit items-center justify-center p-1 hover:bg-gray-50')}>
              <ChevronDownIcon
                className={twMerge(
                  'transition-transform duration-200 ease-in-out',
                  isExpanded && 'rotate-180',
                )}
              />
            </div>
          </div>
        </div>
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              <div
                className="flex cursor-default flex-col overflow-x-auto rounded-md bg-white px-2 pt-3 shadow-sm"
                onClick={(e) => e.stopPropagation()}
              >
                {tableBodyConstructor[type]({
                  ...event,
                  selectedCustomDateFilter,
                  selectedDateFilter,
                  // Pool rows have no event form to preview: View Details opens the pool, as the
                  // card menu does.
                  onView: isPoolEvent(event) ? handleOpenEditPoolModal : onTransactionView,
                  onDelete: onTransactionDelete,
                  onEdit: onTransactionEdit,
                  companyId,
                  name: typeof name === 'string' ? name : typeof name === 'function' ? name : '', //weird
                  inCurrentRound: event.canDelete && event.canEdit,
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
